import { createContext, useContext, useState, useRef, useCallback } from "react";
import axios from "axios";
import { isIndia } from "@/lib/edition";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Regional languages offered on the India edition. English is the default / reset.
export const LANGS = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "bn", label: "Bengali", native: "বাংলা" },
  { code: "te", label: "Telugu", native: "తెలుగు" },
  { code: "mr", label: "Marathi", native: "मराठी" },
  { code: "ta", label: "Tamil", native: "தமிழ்" },
  { code: "ur", label: "Urdu", native: "اردو" },
  { code: "gu", label: "Gujarati", native: "ગુજરાતી" },
  { code: "kn", label: "Kannada", native: "ಕನ್ನಡ" },
  { code: "ml", label: "Malayalam", native: "മലയാളം" },
  { code: "or", label: "Odia", native: "ଓଡ଼ିଆ" },
  { code: "pa", label: "Punjabi", native: "ਪੰਜਾਬੀ" },
  { code: "as", label: "Assamese", native: "অসমীয়া" },
  { code: "mai", label: "Maithili", native: "मैथिली" },
  { code: "sat", label: "Santali", native: "ᱥᱟᱱᱛᱟᱲᱤ" },
  { code: "ks", label: "Kashmiri", native: "کٲشُر" },
  { code: "ne", label: "Nepali", native: "नेपाली" },
  { code: "kok", label: "Konkani", native: "कोंकणी" },
  { code: "sd", label: "Sindhi", native: "سنڌي" },
  { code: "doi", label: "Dogri", native: "डोगरी" },
  { code: "mni", label: "Manipuri", native: "মৈতৈলোন্" },
];

export const langLabel = (code) => (LANGS.find((l) => l.code === code)?.native) || "English";

function loadCache(lang) {
  try { return JSON.parse(localStorage.getItem("ia_tr_" + lang) || "{}"); } catch { return {}; }
}
function saveCache(lang, dict) {
  try { localStorage.setItem("ia_tr_" + lang, JSON.stringify(dict)); } catch { /* ignore */ }
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (!isIndia) return "en";
    try {
      const p = new URLSearchParams(window.location.search).get("lang");
      if (p && LANGS.some((l) => l.code === p)) return p;
      const stored = localStorage.getItem("ia_lang");
      return LANGS.some((l) => l.code === stored) ? stored : "en";
    } catch { return "en"; }
  });
  const [dict, setDict] = useState(() => (lang === "en" ? {} : loadCache(lang)));
  const [translating, setTranslating] = useState(false);

  const dictRef = useRef(dict);
  dictRef.current = dict;
  const pending = useRef(new Set());
  const timer = useRef(null);
  const inflight = useRef(0);

  const persist = (l, d) => {
    // Persist ONLY real translations — never cache an English fallback (that would
    // permanently mask a string that simply hasn't been translated yet).
    saveCache(l, Object.fromEntries(Object.entries(d).filter(([k, v]) => v && v !== k)));
  };

  const flush = useCallback(() => {
    const all = Array.from(pending.current);
    pending.current.clear();
    if (!all.length || lang === "en") return;
    const CHUNK = 8;
    const chunks = [];
    for (let i = 0; i < all.length; i += CHUNK) chunks.push(all.slice(i, i + CHUNK));
    inflight.current += chunks.length;
    setTranslating(true);
    chunks.forEach((texts) => {
      axios.post(`${API}/i18n/translate`, { lang, texts }, { timeout: 60000 })
        .then(({ data }) => {
          const arr = (data && data.translations) || [];
          setDict((prev) => {
            const next = { ...prev };
            texts.forEach((s, i) => { const tr = arr[i]; next[s] = (tr && tr !== s) ? tr : s; });
            persist(lang, next);
            return next;
          });
        })
        .catch(() => {
          // Mark attempted (English) in-memory so we don't re-queue this session,
          // but don't persist — it will retry on a later visit.
          setDict((prev) => {
            const next = { ...prev };
            texts.forEach((s) => { if (next[s] === undefined) next[s] = s; });
            return next;
          });
        })
        .finally(() => {
          inflight.current -= 1;
          if (inflight.current <= 0) { inflight.current = 0; setTranslating(false); }
        });
    });
  }, [lang]);

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 120);
  }, [flush]);

  // t(text): returns the translated string if available, else queues it and
  // returns the English source as a graceful fallback until the batch resolves.
  const t = useCallback((text) => {
    if (lang === "en" || !text || typeof text !== "string") return text;
    const d = dictRef.current;
    if (d[text] !== undefined) return d[text];
    if (!pending.current.has(text)) { pending.current.add(text); schedule(); }
    return text;
  }, [lang, schedule]);

  const setLang = useCallback((code) => {
    const next = LANGS.some((l) => l.code === code) ? code : "en";
    setLangState(next);
    try { localStorage.setItem("ia_lang", next); } catch { /* ignore */ }
    if (next === "en") { pending.current.clear(); setDict({}); }
    else { setDict(loadCache(next)); }
  }, []);

  const resetToEnglish = useCallback(() => setLang("en"), [setLang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, resetToEnglish, t, langs: LANGS, enabled: isIndia, translating }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext) || {
    lang: "en", setLang: () => {}, resetToEnglish: () => {}, t: (x) => x, langs: LANGS, enabled: false, translating: false,
  };
}
